import { useState, useContext } from "react";
import { useFinancials } from "../../hooks/useFinancials";
import RevenueChart from "./RevenueChart";
import ExpenseChart from "./ExpenseChart";
import { exportToCSV, exportToPDF, calcInvoiceTotal } from "../../services/financialReportService";
import { CurrentUserContext } from "../../contexts/CurrentUserContext";
import { showErrorToast } from '../../utils/toast';   // Block 233
import { localToday } from "../../utils/invoiceOverdue";
import {
  PageLayout, PageHeader, Card, DashboardCard, StatTile, ProgressBar, SegmentedControl, Menu, OverflowMenu, Button,
  EmptyState, ErrorState, Skeleton, Field, DateInput, StatusPill, useBreakpoint, v, HeaderButton,
} from "../../ui";
import { fmtMoney, setMoneyBase, plusOthers } from "./money";
import "./financials.css";
import { setDeepLink } from "../../utils/deepLink";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDate(str) { return new Date(str + "T12:00:00"); }

function fmtDate(str) {
  if (!str) return "—";
  return toDate(str).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// "1 – 31 Oct 2026", "1 Jul – 30 Sep 2026", "1 Apr 2026 – 31 Mar 2027".
export function fmtRange(start, end) {
  if (!start || !end) return "";
  const a = toDate(start); const b = toDate(end);
  const d = (x, o) => x.toLocaleDateString("en-GB", o);
  if (a.getFullYear() !== b.getFullYear()) return `${fmtDate(start)} – ${fmtDate(end)}`;
  if (a.getMonth() !== b.getMonth()) return `${d(a, { day: "numeric", month: "short" })} – ${fmtDate(end)}`;
  if (a.getDate() !== b.getDate()) return `${a.getDate()} – ${fmtDate(end)}`;
  return fmtDate(end);
}

function daysBetween(a, b) {
  return Math.round((toDate(b) - toDate(a)) / 86_400_000);
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const PRESETS = [
  { value: "this-month", label: "This month" },
  { value: "last-month", label: "Last month" },
  { value: "this-quarter", label: "This quarter" },
  { value: "last-quarter", label: "Last quarter" },
  { value: "this-year", label: "Tax year" },
  { value: "last-year", label: "Last year" },
  { value: "custom", label: "Custom" },
];

const CAP = 6;

// ─── Export ───────────────────────────────────────────────────────────────────

function useExport({ invoices, expenses, start, end, allProjects }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  // Grab company profile from context for the PDF header
  const { currentUser } = useContext(CurrentUserContext) || {};
  const profile = currentUser?.preferences || {};

  async function handlePDF() {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await exportToPDF(invoices, expenses, start, end, profile, allProjects || []);
    } catch (err) {
      console.error('PDF export failed:', err);
      showErrorToast('Could not generate PDF, please try again.');
    } finally {
      setPdfBusy(false);
    }
  }

  function handleCSV() {
    exportToCSV(invoices, expenses, start, end, allProjects || []);
  }

  return {
    pdfBusy,
    items: [
      { label: pdfBusy ? "Generating PDF…" : "PDF report", icon: "📄", onSelect: handlePDF },
      { label: "CSV for accountant", icon: "📊", onSelect: handleCSV },
    ],
  };
}

// ─── Net profit hero ──────────────────────────────────────────────────────────

function NetProfitCard({ metrics }) {
  const { netProfit, totalInvoiced, totalReceived, totalOutstanding } = metrics;
  const recPct = totalInvoiced > 0 ? Math.round((totalReceived / totalInvoiced) * 100) : 0;
  return (
    <DashboardCard tint="pastel-mint" title="Net profit" meta="Received minus expenses" className="fin-hero">
      <span className="fin-hero__value" data-testid="fin-net">{fmtMoney(netProfit)}</span>
      <div className="fin-stack">
        <ProgressBar
          label="Invoiced received and still to come in"
          valueText=""
          max={totalInvoiced}
          segments={[
            { label: "Received", value: totalReceived, color: "pastel-green" },
            { label: "Outstanding", value: Math.max(0, totalOutstanding), color: "pastel-orange" },
          ]}
        />
        <div className="fin-legend">
          <span><span className="fin-key" style={{ '--fin-tint': v("pastel-green") }} aria-hidden="true" />{recPct}% of invoiced received</span>
          <span><span className="fin-key" style={{ '--fin-tint': v("pastel-orange") }} aria-hidden="true" />{fmtMoney(totalOutstanding)}{plusOthers(metrics.others?.outstanding) ? ` ${plusOthers(metrics.others.outstanding)}` : ''} still to come in</span>
        </div>
      </div>
    </DashboardCard>
  );
}

// Decision 72: the base-currency total, then any other currency on its own line ("plus €1,200").
function Plus({ amount, others, testId }) {
  const more = plusOthers(others);
  return (
    <>
      <span data-testid={testId}>{fmtMoney(amount)}</span>
      {more && <span className="fin-plus" data-testid={testId ? `${testId}-plus` : undefined}>{more}</span>}
    </>
  );
}

function KpiTiles({ metrics }) {
  const outstandingHint = metrics.totalOverdue > 0
    ? `${fmtMoney(metrics.totalOverdue)} overdue`
    : metrics.totalOutstanding > 0 ? "None overdue from this period" : "All clear";   // the attention list covers every period
  return (
    <div className="fin-kpis">
      <StatTile card color="pastel-periwinkle" label="Invoiced" value={<Plus amount={metrics.totalInvoiced} others={metrics.others?.invoiced} />} hint={plural(metrics.invoiceCount, "invoice")} />
      <StatTile card color="pastel-mint" label="Received" value={<Plus amount={metrics.totalReceived} others={metrics.others?.received} />} hint={`${metrics.paidCount} paid`} />
      <StatTile card color="pastel-orange" label="Outstanding" value={<Plus amount={metrics.totalOutstanding} others={metrics.others?.outstanding} testId="fin-outstanding" />} hint={outstandingHint} />
      <StatTile card color="pastel-pink" label="Expenses" value={fmtMoney(metrics.totalExpenses)} hint={metrics.expenseCount > 0 ? "Logged this period" : "None logged"} />
    </div>
  );
}

// ─── Needs your attention ─────────────────────────────────────────────────────

function AllClear({ title, hint }) {
  return (
    <div className="fin-clear">
      <span className="fin-clear__tick" aria-hidden="true">✓</span>
      <span className="fin-clear__text"><strong>{title}</strong><span>{hint}</span></span>
    </div>
  );
}

function AttentionCard({ title, color, count, testId, children }) {
  return (
    <DashboardCard tint={color} title={title} data-testid={testId}
      meta={<span className="fin-count" aria-label={`${count} ${count === 1 ? "item" : "items"}`}>{count}</span>}>
      {children}
    </DashboardCard>
  );
}

function MoreRow({ n, onNavigate }) {
  if (n <= 0) return null;
  return (
    <li className="fin-item fin-item--more">
      <Button variant="tertiary" size="sm" onClick={() => onNavigate && onNavigate("Invoices")}>
        +{n} more in Invoicing
      </Button>
    </li>
  );
}

function OutstandingActions({ overdueInvoices, upcomingInvoices, unbilledExpenses, onNavigateInvoicing, allProjects }) {
  const today = localToday();
  const projectName = pid => {
    if (!pid) return "No project";
    const p = allProjects.find(p => String(p.id) === String(pid));
    return p ? p.name : "No project";
  };
  const view = () => onNavigateInvoicing && onNavigateInvoicing("Invoices");

  return (
    <div className="fin-attention">
      <AttentionCard title="Overdue invoices" color="pastel-pink" count={overdueInvoices.length} testId="fin-overdue">
        {overdueInvoices.length === 0 ? (
          <AllClear title="Nothing overdue" hint="Every client is paying on time." />
        ) : (
          <ul className="fin-items">
            {overdueInvoices.slice(0, CAP).map(inv => {
              const daysOver = daysBetween(inv.dueDate, today);
              const email = inv.contactEmail || "";
              const subject = encodeURIComponent(`Following up on invoice ${inv.number}`);
              return (
                <li key={inv.id} className="fin-item">
                  <span className="fin-item__main">
                    <strong>{inv.contactName || inv.contactCompany || inv.number}</strong>
                    <span>{plural(daysOver, "day")} overdue</span>
                  </span>
                  <span className="fin-item__amt">{fmtMoney(calcInvoiceTotal(inv.lineItems), inv.currency)}</span>
                  {email ? (
                    <a className="ui-btn ui-btn--secondary ui-btn--sm fin-item__act" href={`mailto:${email}?subject=${subject}&body=`}>Chase</a>
                  ) : (
                    <Button variant="tertiary" size="sm" className="fin-item__act" onClick={view}>View</Button>
                  )}
                </li>
              );
            })}
            <MoreRow n={overdueInvoices.length - CAP} onNavigate={onNavigateInvoicing} />
          </ul>
        )}
      </AttentionCard>

      <AttentionCard title="Due in 14 days" color="pastel-orange" count={upcomingInvoices.length} testId="fin-upcoming">
        {upcomingInvoices.length === 0 ? (
          <AllClear title="Nothing due soon" hint="No payments expected in the next two weeks." />
        ) : (
          <ul className="fin-items">
            {upcomingInvoices.slice(0, CAP).map(inv => {
              const daysLeft = daysBetween(today, inv.dueDate);
              return (
                <li key={inv.id} className="fin-item">
                  <span className="fin-item__main">
                    <strong>{inv.contactName || inv.contactCompany || inv.number}</strong>
                    <span>{daysLeft <= 0 ? "Due today" : `Due in ${plural(daysLeft, "day")}`} · {fmtDate(inv.dueDate)}</span>
                  </span>
                  <span className="fin-item__amt">{fmtMoney(calcInvoiceTotal(inv.lineItems), inv.currency)}</span>
                  <Button variant="tertiary" size="sm" className="fin-item__act" onClick={view}>View</Button>
                </li>
              );
            })}
            <MoreRow n={upcomingInvoices.length - CAP} onNavigate={onNavigateInvoicing} />
          </ul>
        )}
      </AttentionCard>

      <AttentionCard title="Unbilled expenses" color="pastel-blue" count={unbilledExpenses.length} testId="fin-unbilled">
        {unbilledExpenses.length === 0 ? (
          <AllClear title="All billed" hint="No expenses waiting to be recharged." />
        ) : (
          <ul className="fin-items">
            {unbilledExpenses.slice(0, CAP).map(exp => (
              <li key={exp.id} className="fin-item">
                <span className="fin-item__main">
                  <strong>{exp.title}</strong>
                  <span>{projectName(exp.project_id)}</span>
                </span>
                <span className="fin-item__amt">{fmtMoney(exp.amount)}</span>
                <Button variant="tertiary" size="sm" className="fin-item__act" onClick={view}>Add to invoice</Button>
              </li>
            ))}
            <MoreRow n={unbilledExpenses.length - CAP} onNavigate={onNavigateInvoicing} />
          </ul>
        )}
      </AttentionCard>
    </div>
  );
}

// ─── Tax year ─────────────────────────────────────────────────────────────────

function TaxYearCard({ summary }) {
  if (!summary) return null;
  const {
    taxYearLabel, monthOfTaxYear, yearElapsed = 0,
    ytdInvoiced, ytdReceived, ytdExpenses, ytdNet, projectedInvoiced, lastYearInvoiced,
  } = summary;
  const soFarPct = projectedInvoiced > 0 ? Math.round((ytdInvoiced / projectedInvoiced) * 100) : 0;
  const tiles = [
    { label: "YTD invoiced", value: ytdInvoiced, tint: "pastel-yellow" },
    { label: "YTD received", value: ytdReceived, tint: "pastel-mint" },
    { label: "YTD expenses", value: ytdExpenses, tint: "pastel-pink" },
    { label: "YTD net", value: ytdNet, tint: "surface-2" },
  ];
  let comparison = "No figures for last tax year yet, so there's nothing to compare against.";
  if (lastYearInvoiced > 0) {
    const diff = Math.round(((projectedInvoiced - lastYearInvoiced) / lastYearInvoiced) * 100);
    comparison = `Last tax year you invoiced ${fmtMoney(lastYearInvoiced)}. This year is on course for `
      + (diff === 0 ? "about the same." : `${Math.abs(diff)}% ${diff > 0 ? "more" : "less"}.`);
  }
  return (
    <DashboardCard tint="pastel-periwinkle" title={`Tax year ${taxYearLabel}`}
      meta={`April – March${monthOfTaxYear ? ` · month ${monthOfTaxYear} of 12` : ""}`}>
      <div className="fin-ytd">
        {tiles.map(t => (
          <div key={t.label} className="fin-tint" style={{ '--fin-tint': v(t.tint) }}>
            <StatTile label={t.label} value={fmtMoney(t.value)} />
          </div>
        ))}
      </div>
      <div className="fin-proj">
        <div className="fin-proj__head">
          <span className="fin-proj__label">Projected full year: <strong>{fmtMoney(projectedInvoiced)}</strong></span>
          <span className="fin-muted">{fmtMoney(ytdInvoiced)} invoiced so far · {soFarPct}%</span>
        </div>
        <div className="fin-proj__bar">
          <ProgressBar label="Invoiced so far against the projected full year" valueText="" value={ytdInvoiced} max={projectedInvoiced} color="pastel-periwinkle" />
          <span className="fin-proj__today" style={{ left: `${Math.round(yearElapsed * 1000) / 10}%` }} title="Today" aria-hidden="true" />
        </div>
        <span className="fin-muted">{comparison}</span>
      </div>
      <p className="fin-disclaimer">
        Projections are estimates based on your current data. Always check tax and compliance questions with your accountant.
      </p>
    </DashboardCard>
  );
}

// ─── Income by project ────────────────────────────────────────────────────────

function PaidBar({ p }) {
  return (
    <div className="fin-paidbar">
      <ProgressBar
        label={`${p.name}: paid and outstanding`}
        valueText=""
        max={p.invoiced}
        segments={[
          { label: "Paid", value: p.received, color: "pastel-green" },
          { label: "Outstanding", value: Math.max(0, p.invoiced - p.received), color: "pastel-orange" },
        ]}
      />
    </div>
  );
}

function ProjectName({ p, onOpenProjectFinancials }) {
  const name = onOpenProjectFinancials && p.id
    ? <button type="button" className="fin-linkbtn" onClick={() => onOpenProjectFinancials(p.id)}>{p.name}</button>
    : <span>{p.name}</span>;
  return (
    <span className="fin-projname">
      <span className="fin-tag">{p.tag || (p.id ? "Project" : "None")}</span>
      {name}
    </span>
  );
}

const netPill = net => <StatusPill tone={net >= 0 ? "success" : "warning"}>{fmtMoney(net)}</StatusPill>;

function IncomeByProject({ byProject, invoices, onOpenProjectFinancials, onNavigate }) {
  const phone = useBreakpoint() === "phone";
  const allUnattached = invoices.length > 0 && invoices.every(i => !i.project_id);

  return (
    <DashboardCard tint="pastel-blue" title="Income by project" data-testid="fin-projects"
      meta={(
        <div className="fin-legend">
          <span><span className="fin-key" style={{ '--fin-tint': v("pastel-green") }} aria-hidden="true" />Paid</span>
          <span><span className="fin-key" style={{ '--fin-tint': v("pastel-orange") }} aria-hidden="true" />Outstanding</span>
        </div>
      )}>

      {byProject.length === 0 ? (
        <EmptyState title="No income in this period">Invoices you send in this period show here, by project.</EmptyState>
      ) : phone ? (
        <ul className="fin-projcards">
          {byProject.map(p => (
            <li key={p.id ?? p.name} className="fin-projcard">
              <ProjectName p={p} onOpenProjectFinancials={onOpenProjectFinancials} />
              <PaidBar p={p} />
              <dl className="fin-projcard__nums">
                <div><dt>Invoiced</dt><dd>{fmtMoney(p.invoiced)}</dd></div>
                <div><dt>Paid</dt><dd>{fmtMoney(p.received)}</dd></div>
                <div><dt>Expenses</dt><dd>{fmtMoney(p.expenses)}</dd></div>
                <div><dt>Net</dt><dd>{netPill(p.net)}</dd></div>
              </dl>
            </li>
          ))}
        </ul>
      ) : (
        <div className="fin-table-wrap">
          <div className="fin-table" role="table" aria-label="Income by project">
            <div className="fin-tr fin-tr--head" role="row">
              <span role="columnheader">Project</span>
              <span role="columnheader"><span className="ui-visually-hidden">Paid and outstanding</span></span>
              <span role="columnheader">Invoiced</span>
              <span role="columnheader">Paid</span>
              <span role="columnheader">Outstanding</span>
              <span role="columnheader">Expenses</span>
              <span role="columnheader">Net</span>
            </div>
            {byProject.map(p => (
              <div key={p.id ?? p.name} className="fin-tr" role="row">
                <span role="cell"><ProjectName p={p} onOpenProjectFinancials={onOpenProjectFinancials} /></span>
                <span role="cell"><PaidBar p={p} /></span>
                <span role="cell" className="fin-num fin-num--strong">{fmtMoney(p.invoiced)}</span>
                <span role="cell" className="fin-num">{fmtMoney(p.received)}</span>
                <span role="cell" className="fin-num">{fmtMoney(p.invoiced - p.received)}</span>
                <span role="cell" className="fin-num">{fmtMoney(p.expenses)}</span>
                <span role="cell" className="fin-num">{netPill(p.net)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {allUnattached && (
        <div className="fin-cta" data-testid="fin-unattached-cta">
          <span>All income is unattached. Link invoices to projects to see which work pays best.</span>
          <Button variant="create" size="sm" onClick={() => onNavigate && onNavigate("Invoices")}>Link to projects</Button>
        </div>
      )}
    </DashboardCard>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * FinancialDashboard
 *
 * Props:
 *   onNavigate — optional callback(filterKey) from App.js
 *                Used by "+ New invoice", "View", "Add to invoice" and "Link to projects"
 *   onOpenProjectFinancials — optional callback(projectId)
 *                Used by project names in the income breakdown
 */
export default function FinancialDashboard({ onNavigate, onOpenProjectFinancials }) {
  const phone = useBreakpoint() === "phone";
  const {
    preset, setPreset,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    start, end,
    metrics,
    trendData,
    byProject,
    byCategory,
    annualSummary,
    overdueInvoices,
    upcomingInvoices,
    unbilledExpenses,
    allProjects,
    invoices,
    exportInvoices,
    expenses,
    invoicesLoaded = true,
    invoicesFailed = false,
    retry,
  } = useFinancials();

  setMoneyBase(metrics?.currency);   // decision 72: sums are in the base currency
  const exp = useExport({ invoices: exportInvoices, expenses, start, end, allProjects });
  // Opens the Invoices page with its new-invoice form open (deep link invoice_new).
  const newInvoice = () => { setDeepLink("invoice_new", "1"); if (onNavigate) onNavigate("Invoices"); };

  const createBtn = <HeaderButton primary onClick={newInvoice}>+ New invoice</HeaderButton>;
  const actions = phone ? (
    <>
      {createBtn}
      <OverflowMenu label="Export report" title="Export report" items={exp.items} buttonClassName="ui-btn ui-btn--secondary ui-page-header__more" />
    </>
  ) : (
    <>
      <Menu label="Export report" title="Export report" items={exp.items} buttonClassName="ui-btn ui-btn--secondary">
        {exp.pdfBusy ? "Generating…" : "Export report ▾"}
      </Menu>
      {createBtn}
    </>
  );

  const periodPicker = (
    <div className="fin-period">
      <div className="fin-period__scroll">
        <SegmentedControl label="Period" options={PRESETS} value={preset} onChange={setPreset} />
      </div>
      {preset === "custom" && (
        <div className="fin-period__custom">
          <Field label="From"><DateInput value={customStart} onChange={e => setCustomStart(e.target.value)} /></Field>
          <Field label="To"><DateInput value={customEnd} onChange={e => setCustomEnd(e.target.value)} /></Field>
        </div>
      )}
    </div>
  );

  const header = (
    <PageHeader
      eyebrow={{ label: "Business workspace", color: "group-overview" }}
      title="Financial overview"
      subline={fmtRange(start, end)}
      primary={actions}
    />
  );

  // A failed load must never read as "nothing overdue" (InvoicesContext loadFailed).
  if (invoicesFailed) {
    return (
      <PageLayout className="ui-page fin-page">
        {header}
        <ErrorState
          title="Your invoices didn't load"
          message="Without them every figure here would be wrong, so none are shown. Check your connection and try again."
          onRetry={retry}
        />
      </PageLayout>
    );
  }

  if (!invoicesLoaded) {
    return (
      <PageLayout className="ui-page fin-page" aria-busy="true">
        {header}
        {periodPicker}
        <div className="fin-top">
          <Skeleton height={200} radius="var(--radius-card)" label="Loading your figures" />
          <div className="fin-kpis">
            {[0, 1, 2, 3].map(i => <Skeleton key={i} height={110} radius="var(--radius-card)" label="Loading" />)}
          </div>
        </div>
        <Skeleton height={280} radius="var(--radius-card)" label="Loading" />
      </PageLayout>
    );
  }

  return (
    <PageLayout className="ui-page fin-page">
      <div className="fin-headwrap">
        {header}
        {periodPicker}
      </div>

      <section className="fin-top" aria-label="This period">
        <NetProfitCard metrics={metrics} />
        <KpiTiles metrics={metrics} />
      </section>

      <section className="fin-section" aria-labelledby="fin-attention-h">
        <h2 id="fin-attention-h" className="fin-h2">Needs your attention</h2>
        <OutstandingActions
          overdueInvoices={overdueInvoices}
          upcomingInvoices={upcomingInvoices}
          unbilledExpenses={unbilledExpenses}
          onNavigateInvoicing={onNavigate}
          allProjects={allProjects}
        />
      </section>

      <Card lift={false}>
        <RevenueChart title="Revenue trend" data={trendData} />
      </Card>

      <TaxYearCard summary={annualSummary} />

      <IncomeByProject
        byProject={byProject}
        invoices={invoices}
        onOpenProjectFinancials={onOpenProjectFinancials}
        onNavigate={onNavigate}
      />

      {expenses.length > 0 && (
        <DashboardCard tint="pastel-pink" title="Expenses by category">
          <ExpenseChart data={byCategory} />
        </DashboardCard>
      )}
    </PageLayout>
  );
}
